import { Api } from 'lib/api.mjs';
import { notificationsQuery } from 'lib/notifications.mjs';

/**
 * The notification centre — history, read state, delivery preferences and the
 * Telegram chat link.
 *
 * Every endpoint here is scoped server-side to the signed-in user; there is no
 * "on behalf of" parameter to pass and none should be added. Preferences are
 * self-serve, which was the client's explicit decision.
 *
 * The query string is built by lib/notifications.mjs, which also owns the page
 * size the pager reads back — spelling it again here would let the two drift,
 * the same reason services/activity defers to lib/activity.
 */

/* ── History ─────────────────────────────────────────────────────────────── */

/**
 * @param {{ offset?: number, types?: string[], q?: string, from?: string,
 *           to?: string, unread?: boolean, signal?: AbortSignal }} [payload]
 */
export function getNotifications({ offset = 0, types = [], q = '', from = '', to = '', unread = false, signal } = {}) {
  return Api.get(`/notifications?${notificationsQuery({ offset, types, q, from, to, unread })}`, { signal });
}

/**
 * Just the badge number.
 *
 * Its own endpoint because this polls and the list does not — pulling a page of
 * rows every 30 seconds to render one integer would be wasteful.
 */
export function getUnreadCount({ signal } = {}) {
  return Api.get('/notifications/unread-count', { signal });
}

/** @param {{ id: number, read?: boolean }} payload */
export function setRead({ id, read = true }) {
  return Api.post(`/notifications/${id}/read`, { body: { read } });
}

export function markAllRead() {
  return Api.post('/notifications/read-all', {});
}

/**
 * Delete one notification permanently.
 *
 * Answers with the recalculated unread count, because deleting an unread row
 * moves the badge — the caller should not have to work out by how much.
 *
 * @param {{ id: number }} payload
 */
export function deleteNotification({ id }) {
  return Api.delete(`/notifications/${id}`);
}

/* ── Preferences ─────────────────────────────────────────────────────────── */

/**
 * The catalogue this user may see, WITH their effective settings.
 *
 * One request rather than two: the UI cannot draw a switch without both its
 * label and its state, and two round trips to render one screen is two chances
 * for them to disagree.
 */
export function getPreferences({ signal } = {}) {
  return Api.get('/notifications/preferences', { signal });
}

/**
 * @param {{ prefs: Object }} payload  Build it with prefPatch/groupPatch from
 *   lib/notifications.mjs — a patch names only the channels that moved, and the
 *   server merges it over the rest.
 */
export function savePreferences(payload) {
  return Api.put('/notifications/preferences', { body: payload });
}

/* ── Telegram ────────────────────────────────────────────────────────────── */

/** Which chats this user has linked. */
export function getTelegramLinks({ signal } = {}) {
  return Api.get('/notifications/telegram', { signal });
}

/**
 * Mint a one-time enrolment code.
 *
 * Short-lived and single-use, but it grants "send this user's alerts to my
 * chat" — so it is shown once and never persisted client-side.
 */
export function createTelegramCode() {
  return Api.post('/notifications/telegram/code', {});
}

/** @param {{ id: number }} payload */
export function unlinkTelegram({ id }) {
  return Api.delete(`/notifications/telegram/${id}`);
}
